import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "@tanstack/react-router";
import { BookUser, CalendarRange, GripVertical, LockKeyhole, LockOpen, UserRoundX } from "lucide-react";
import { TimelineRow } from "@/components/makers/TimelineRow";
import { OfficeFilter } from "@/components/filters/OfficeFilter";
import { useOffices } from "@/hooks/useOffices";
import { useUserSettings } from "@/contexts/UserSettingsContext";
import { useModal } from "@/hooks/useModal";
import type { Assignment, ProjectSummary } from "@/hooks/useProjectBoardData";
import {
  useRecentlyChanged,
  useRecentlyChangedRowsApi,
} from "@/contexts/RecentlyChangedRowsContext";
import { useOoo, type OooContextValue } from "@/contexts/OooContext";
import { displayedOooHours } from "@/utils/oooDisplay";
import { useRoles, type UseRolesResult } from "@/hooks/useRoles";
import { db } from "@/lib/supabase";
import { diffRecords, recordAudit } from "@/lib/audit";
import { assignmentAuditLabel, projectAuditLabel } from "@/utils/auditLabels";
import { withWeeklyHoursSummary } from "@/utils/auditWeeklyHours";
import type { Database } from "@/types/database.types";
import { clearAssignmentEmployees } from "@/utils/assignmentEmployees";
import { canBeConfirmed, withWrittenEmployees } from "@/utils/assignmentConfirmation";
import type { EmployeeBoardUpdate } from "@/utils/findPeoplePending";
import { stashMakersEmployeeFilter } from "@/utils/makersDeepLink";
import { buildWeekAllocations } from "@/utils/makersUtils";
import { formatEmployeeDisplayName } from "@/utils/nameUtils";
import { assignmentFillCapacityHours, getProjectWeekIndex } from "@/utils/projectUtils";
import { addWeeks } from "@/utils/dateUtils";
import type { WeekColumn } from "@/utils/weekUtils";
import { getStartOfWeek } from "@/utils/weekUtils";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FindPeopleModal } from "./FindPeopleModal";
import type { ProjectAvailabilityFocus } from "./ProjectAvailabilityDock";

interface AssignmentRowProps {
  assignment: Assignment;
  weeks: WeekColumn[];
  project: ProjectSummary;
  overAllocatedEmployeeIds: Set<string>;
  employeeProjectLabels?: Map<string, string[]>;
  onRefresh: () => void;
  updateProjectOptimistically: (
    projectId: string,
    updates: Partial<ProjectSummary>
  ) => void;
  refreshSilently: () => Promise<void>;
  onOpenAvailabilityForAssignment?: (focus: ProjectAvailabilityFocus) => void;
  isAvailabilityFocused?: boolean;
}

function buildProjectAllocationForAssignments(
  assignments: Assignment[],
  project: ProjectSummary,
  weeks: WeekColumn[]
) {
  const weekAllocations = buildWeekAllocations(assignments, weeks, project.start_date, project.num_weeks);
  return {
    project: {
      id: project.id,
      name: project.name,
      client: project.client,
      color: project.color,
    },
    projectId: project.id,
    assignments: assignments.map((assignment) => ({
      ...assignment,
      project: {
        id: project.id,
        name: project.name,
        client: project.client,
        color: project.color,
        deal_stage: project.deal_stage,
        start_date: project.start_date,
        num_weeks: project.num_weeks,
      },
    })),
    weekAllocations,
  };
}

function getTotalWeeklyHours(assignments: Assignment[]): number {
  return assignments.reduce((sum, assignment) => {
    const hoursArray = assignment.weekly_hours_allocated ?? [];
    const assignmentTotal = hoursArray.reduce((arrSum, hours) => arrSum + hours, 0);
    return sum + assignmentTotal;
  }, 0);
}

function hasAnyOverAllocatedEmployee(
  assignments: Assignment[],
  overAllocatedEmployeeIds: Set<string>
): boolean {
  return assignments.some((assignment) =>
    assignment.employees.some((employee) => overAllocatedEmployeeIds.has(employee.id))
  );
}

function formatAssignmentHours(assignment: Assignment): string {
  return `${assignment.weekly_hours_allocated ?? 0}h`;
}


function updateAssignmentOptimistically({
  project,
  assignmentId,
  updates,
  updateProjectOptimistically,
}: {
  project: ProjectSummary;
  assignmentId: string;
  updates: Partial<Assignment>;
  updateProjectOptimistically: (projectId: string, updates: Partial<ProjectSummary>) => void;
}) {
  updateProjectOptimistically(project.id, {
    project_assignments: project.project_assignments.map((ass) =>
      ass.id === assignmentId ? { ...ass, ...updates } : ass
    ),
  });
}

const FIND_PEOPLE_OFFICES_KEY = "findPeopleOffices";

function readStoredOffices(fallback: string[]): string[] {
  try {
    const stored = localStorage.getItem(FIND_PEOPLE_OFFICES_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return fallback;
}

function useAssignmentFindPeopleModal({
  assignment,
  project,
  refreshSilently,
  updateProjectOptimistically,
  rolesValue,
  oooValue,
}: {
  assignment: Assignment;
  project: ProjectSummary;
  refreshSilently: () => Promise<void>;
  updateProjectOptimistically: (projectId: string, updates: Partial<ProjectSummary>) => void;
  rolesValue: UseRolesResult;
  oooValue: OooContextValue;
}) {
  const { offices, selectedOffices: navbarOffices } = useOffices();
  const { settings: userSettings, loading: userSettingsLoading } = useUserSettings();
  // Read here, inside the board's provider tree, and handed to the modal.
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const [modalOffices, setModalOffices] = useState<string[]>(() =>
    readStoredOffices(navbarOffices)
  );

  useEffect(() => {
    localStorage.setItem(FIND_PEOPLE_OFFICES_KEY, JSON.stringify(modalOffices));
  }, [modalOffices]);

  const toggleOffice = useCallback((id: string) => {
    setModalOffices((prev) => {
      if (prev.includes(id)) return prev.filter((o) => o !== id);
      return [...prev, id];
    });
  }, []);

  const clearOffices = useCallback(() => {
    setModalOffices([]);
  }, []);

  const assignEmployeesOptimistically = (updates: EmployeeBoardUpdate[]) => {
    // Apply every role's change in one update so saving multiple roles together
    // doesn't clobber (each rebuild starts from the same project snapshot).
    updateProjectOptimistically(project.id, {
      project_assignments: project.project_assignments.map((ass) => {
        const update = updates.find((u) => u.assignmentId === ass.id);
        return update
          ? withWrittenEmployees(ass, update.employees, update.isConfirmed)
          : ass;
      }),
    });
  };

  const officeFilterElement = React.createElement(OfficeFilter, {
    offices,
    selectedOffices: modalOffices,
    onSelectOffice: toggleOffice,
    onClearSelectedOffices: clearOffices,
  });

  const { open } = useModal(
    FindPeopleModal,
    {
      project,
      initialAssignmentId: assignment.id,
      assignEmployeesOptimistically,
      refreshSilently,
      updateProjectOptimistically,
      selectedOffices: modalOffices,
      // Leave undefined until settings load so the modal doesn't lock in the
      // USER_SETTINGS_DEFAULTS value; MakersBoard syncs once the real value arrives.
      defaultAvailabilityMode: userSettingsLoading ? undefined : userSettings.availabilityMode,
      rolesValue,
      oooValue,
      markSelfEdit,
    },
    { title: "Find People", size: "fullscreen", headerContent: officeFilterElement }
  );

  return useCallback(() => {
    // Re-read from localStorage to pick up changes from other buttons
    setModalOffices(readStoredOffices(navbarOffices));
    open();
  }, [navbarOffices, open]);
}

export function AssignmentRow({
  assignment,
  weeks,
  project,
  overAllocatedEmployeeIds,
  employeeProjectLabels,
  updateProjectOptimistically,
  refreshSilently,
  onOpenAvailabilityForAssignment,
  isAvailabilityFocused = false,
}: AssignmentRowProps) {
  const rolesValue = useRoles();
  const { roles, formatRole, roleColorStyle } = rolesValue;
  const oooValue = useOoo();
  const { oooByEmployee } = oooValue;
  const [saving, setSaving] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const roleControlRef = useRef<HTMLDivElement>(null);
  const isFlashing = useRecentlyChanged("project_assignments", assignment.id);
  const { markSelfEdit } = useRecentlyChangedRowsApi();

  useEffect(() => {
    if (!isEditingRole) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (roleControlRef.current && !roleControlRef.current.contains(e.target as Node)) {
        setIsEditingRole(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditingRole]);

  const projectAllocation = useMemo(() => {
    return buildProjectAllocationForAssignments([assignment], project, weeks);
  }, [assignment, project, weeks]);

  const projectEndDate = useMemo(() => {
    if (!project.start_date || !project.num_weeks) return undefined;
    const start = getStartOfWeek(new Date(project.start_date));
    const lastWeekStart = addWeeks(start, project.num_weeks - 1);
    return lastWeekStart.toISOString();
  }, [project.start_date, project.num_weeks]);

  const hasOverAllocatedEmployee = hasAnyOverAllocatedEmployee([assignment], overAllocatedEmployeeIds);

  // Tooltip listing the active/upcoming projects each over-allocated maker on
  // this row is currently allocated to, so the warning explains the conflict.
  const overAllocationTooltip = useMemo(() => {
    const labels = new Set<string>();
    for (const employee of assignment.employees) {
      if (!overAllocatedEmployeeIds.has(employee.id)) continue;
      for (const label of employeeProjectLabels?.get(employee.id) ?? []) {
        labels.add(label);
      }
    }
    if (labels.size === 0) return "Over capacity";
    return `Over capacity — allocated to:\n${Array.from(labels)
      .map((label) => `• ${label}`)
      .join("\n")}`;
  }, [assignment.employees, overAllocatedEmployeeIds, employeeProjectLabels]);

  // Capacity the timeline fill is drawn against. An unassigned role has no
  // employee capacity, so this falls back to a full-time week; otherwise the
  // fill would paint the whole cell regardless of the role's allocated hours.
  const employeeCapacity = assignmentFillCapacityHours(assignment.employees);
  const employeeNames = assignment.employees
    .map((emp) => formatEmployeeDisplayName(emp))
    .filter(Boolean)
    .join(" / ");
  const allEmployeeIds = useMemo(() => {
    const ids = new Set<string>(assignment.employees.map((emp) => emp.id));
    return Array.from(ids);
  }, [assignment.employees]);

  const linkToMakers = allEmployeeIds.length > 0;
  const hasAssignees = assignment.employees.length > 0;

  // Out-of-office (OOO) for this row's employee(s), summed per week. Visual
  // only — read-only here; editing happens on the Makers board. Each person's
  // week is held to their own display threshold before summing, so two people
  // with a little OOO each don't add up to an overlay neither would get alone.
  const oooByWeekKey = useMemo(() => {
    const merged = new Map<number, number>();
    const seen = new Set<string>();
    for (const employee of assignment.employees) {
      if (seen.has(employee.id)) continue;
      seen.add(employee.id);
      const weeks = oooByEmployee.get(employee.id);
      if (!weeks) continue;
      for (const [weekKey, hours] of weeks) {
        const shown = displayedOooHours(hours, employee.weekly_capacity_hours);
        if (shown > 0) merged.set(weekKey, (merged.get(weekKey) ?? 0) + shown);
      }
    }
    return merged.size > 0 ? merged : undefined;
  }, [assignment.employees, oooByEmployee]);

  // data-role keeps the legacy slug (e.g. "delivery-lead") derived from the
  // display name so existing Cypress selectors keep working.
  const roleSlug = formatRole(assignment.required_role_id).toLowerCase().replace(/\s+/g, "-");

  const openFindPeopleModal = useAssignmentFindPeopleModal({
    assignment,
    project,
    rolesValue,
    oooValue,
    refreshSilently,
    updateProjectOptimistically,
  });

  const handleRoleChange = async (newRole: string) => {
    if (newRole === assignment.required_role_id) return;
    setSaving(true);
    try {
      updateAssignmentOptimistically({
        project,
        assignmentId: assignment.id,
        updates: { required_role_id: newRole },
        updateProjectOptimistically,
      });
      markSelfEdit("project_assignments", assignment.id);
      const { error } = await db.projectAssignments.update(assignment.id, {
        required_role_id: newRole,
      });
      if (error) {
        console.error("Failed to update assignment role:", error);
        await refreshSilently();
      } else {
        recordAudit({
          table: "project_assignments",
          recordId: assignment.id,
          operation: "UPDATE",
          changes: diffRecords(
            { required_role: formatRole(assignment.required_role_id) },
            { required_role: formatRole(newRole) }
          ),
          recordLabel: assignmentAuditLabel(projectAuditLabel(project), assignment, formatRole),
          officeId: project.office_id ?? null,
        });
      }
    } catch (e) {
      console.error("Error updating assignment role:", e);
      try {
        await refreshSilently();
      } catch (refreshErr) {
        console.error("Error refreshing after role update failure:", refreshErr);
      }
    } finally {
      setSaving(false);
    }
  };

  const canConfirm = canBeConfirmed(assignment.employees.length);
  const handleConfirmChange = async (checked: boolean) => {
    if (!canConfirm) return;
    setSaving(true);
    try {
      updateAssignmentOptimistically({
        project,
        assignmentId: assignment.id,
        updates: { is_confirmed: checked },
        updateProjectOptimistically,
      });
      markSelfEdit("project_assignments", assignment.id);
      const { error } = await db.projectAssignments.update(assignment.id, {
        is_confirmed: checked,
      });
      if (error) {
        console.error("Failed to update assignment confirmed:", error);
        await refreshSilently();
      } else {
        recordAudit({
          table: "project_assignments",
          recordId: assignment.id,
          operation: "UPDATE",
          changes: diffRecords(
            { is_confirmed: assignment.is_confirmed },
            { is_confirmed: checked }
          ),
          recordLabel: assignmentAuditLabel(projectAuditLabel(project), assignment, formatRole),
          officeId: project.office_id ?? null,
        });
      }
    } catch (e) {
      console.error("Error updating assignment confirmed:", e);
      try {
        await refreshSilently();
      } catch (refreshErr) {
        console.error("Error refreshing after confirm update failure:", refreshErr);
      }
    } finally {
      setSaving(false);
    }
  };

  // Clears every assignee on the role in one click, with no confirmation: the
  // audit log is the recovery path and re-assigning is cheap. Deleting the role
  // itself now lives in the Find People modal's tab close button.
  const handleClearAssignees = async () => {
    if (!hasAssignees || saving) return;
    setSaving(true);
    const nameById = new Map<string, string>();
    for (const employee of assignment.employees) {
      const displayName = formatEmployeeDisplayName(employee);
      if (displayName) nameById.set(employee.id, displayName);
    }
    updateAssignmentOptimistically({
      project,
      assignmentId: assignment.id,
      updates: { employees: [], is_confirmed: false },
      updateProjectOptimistically,
    });
    // clearAssignmentEmployees marks the self-edit before it writes.
    await clearAssignmentEmployees(
      assignment,
      markSelfEdit,
      () => {
        setSaving(false);
      },
      () => {
        void refreshSilently();
      },
      {
        recordLabel: assignmentAuditLabel(projectAuditLabel(project), assignment, formatRole),
        nameById,
        officeId: project.office_id ?? null,
      },
    );
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: assignment.id });

  const sortableStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const updateHoursForAssignments = async (
    targetAssignment: Assignment,
    weekKey: number,
    newHours: number,
    singleCellOnly: boolean
  ) => {
    const projectWeekIndex = getProjectWeekIndex(weekKey, weeks, project.start_date, project.num_weeks);
    if (projectWeekIndex === null) return;

    const timelineWeek = weeks.find(w => w.start.getTime() === weekKey);
    if (timelineWeek) {
      const projectStart = new Date(project.start_date);
      const projectStartWeek = getStartOfWeek(projectStart);
      const timelineWeekStart = getStartOfWeek(timelineWeek.start);
      if (timelineWeekStart.getTime() < projectStartWeek.getTime()) {
        return;
      }
    }

    setSaving(true);

    const currentHours = targetAssignment.weekly_hours_allocated ?? [];
    const hoursArray = Array.from({ length: project.num_weeks }, (_, index) => {
      if (singleCellOnly) {
        // Only update the specific cell
        if (index === projectWeekIndex) return newHours;
        return currentHours[index] ?? 0;
      }
      // Default: fill-forward — update this cell and all following weeks
      if (index >= projectWeekIndex) return newHours;
      return currentHours[index] ?? 0;
    });

    const updateData: Database["public"]["Tables"]["project_assignments"]["Update"] = {
      weekly_hours_allocated: hoursArray,
    };

    updateProjectOptimistically(project.id, {
      project_assignments: project.project_assignments.map((assignment) =>
        assignment.id === targetAssignment.id ? { ...assignment, ...updateData } : assignment
      ),
    });

    markSelfEdit("project_assignments", targetAssignment.id);
    const { error } = await db.projectAssignments.update(targetAssignment.id, updateData);

    if (error) {
      console.error("Failed to update assignment hours:", error);
      await refreshSilently();
    } else {
      recordAudit({
        table: "project_assignments",
        recordId: targetAssignment.id,
        operation: "UPDATE",
        changes: withWeeklyHoursSummary(
          diffRecords(
            { weekly_hours_allocated: targetAssignment.weekly_hours_allocated ?? [] },
            { weekly_hours_allocated: hoursArray }
          ),
          project.start_date
        ),
        recordLabel: assignmentAuditLabel(projectAuditLabel(project), targetAssignment, formatRole),
        officeId: project.office_id ?? null,
      });
    }

    setSaving(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      className={`assignment-row ${hasOverAllocatedEmployee ? "assignment-row--warning" : ""} ${isAvailabilityFocused ? "assignment-row--availability-focused" : ""} ${isFlashing ? "row--flashing" : ""} ${isDragging ? "assignment-row--is-dragging" : ""}`}
      data-testid="assignment-row"
      data-role={roleSlug}
      data-assignment-id={assignment.id}
    >
      <div className="assignment-row__fixed">
        <div className="assignment-row__fixed-main assignment-row--main">
          <button
            className="assignment-row__drag-handle"
            {...listeners}
            {...attributes}
            type="button"
            aria-label="Drag to reorder role"
          >
            <GripVertical size={14} />
          </button>
          <div className="assignment-row__role">
            <div className="assignment-row__info">
              <div
                className={`assignment-row__title ${assignment.employees.length > 0 ? "" : "assignment-row__unassigned"}`}
                data-testid="assignment-title"
              >
                {employeeNames ? (
                  <>
                    {canConfirm && (
                      <button
                        className="assignment-row__confirm"
                        data-testid="confirm-assignment-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleConfirmChange(!(assignment.is_confirmed ?? false));
                        }}
                        disabled={saving}
                        aria-label={`Mark ${employeeNames} as ${assignment.is_confirmed ? "unconfirmed" : "confirmed"} for this assignment`}
                        title={assignment.is_confirmed ? "Confirmed — click to unlock" : "Unconfirmed — click to lock"}
                        type="button"
                      >
                        {assignment.is_confirmed ? (
                          <LockKeyhole size={14} />
                        ) : (
                          <LockOpen size={14} />
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      className="assignment-row__name-button"
                      data-testid="assign-role-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFindPeopleModal();
                      }}
                      aria-label="Find people to assign"
                      title="Find people"
                    >
                      {employeeNames}
                    </button>
                    {hasOverAllocatedEmployee && (
                      <span
                        className="assignment-row__over-allocated-badge"
                        title={overAllocationTooltip}
                        aria-label={overAllocationTooltip}
                      >
                        ⚠️
                      </span>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    className="assignment-row__name-button"
                    data-testid="assign-role-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openFindPeopleModal();
                    }}
                    aria-label="Find people to assign"
                    title="Find people"
                  >
                    Unassigned
                  </button>
                )}
              </div>

              <div className="assignment-row__details">
                <div className="assignment-row__detail-rows">
                  <div className="assignment-row__subtitle" ref={roleControlRef}>
                    {isEditingRole ? (
                      <select
                        className="project-card__inline-select project-card__inline-select--small"
                        value={assignment.required_role_id}
                        onChange={(e) => {
                          e.stopPropagation();
                          void handleRoleChange(e.target.value);
                        }}
                        onBlur={() => setIsEditingRole(false)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={saving}
                        autoFocus
                        aria-label="Role"
                      >
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.display_name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className="project-card__editable assignment-row__role-value"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsEditingRole(true);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setIsEditingRole(true);
                          }
                        }}
                        aria-label={`Role: ${formatRole(assignment.required_role_id)}. Click to edit.`}
                      >
                        <span className="role-badge" style={roleColorStyle(assignment.required_role_id)}>
                          {formatRole(assignment.required_role_id)}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Calendar first: it is the one icon present in both row states, so no
              icon shifts position between assigned and unassigned rows. The other
              two render disabled rather than hidden for the same reason. */}
          <div className="assignment-row__action">
            {onOpenAvailabilityForAssignment ? (
              <button
                className={`icon-button ${isAvailabilityFocused ? "icon-button--active" : ""}`}
                type="button"
                title="Show availability for this role"
                aria-label="Show availability for this role"
                aria-pressed={isAvailabilityFocused}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenAvailabilityForAssignment({ project, assignment });
                }}
              >
                <CalendarRange size={16} />
              </button>
            ) : null}
            {linkToMakers ? (
              <Link
                to="/makers"
                className="icon-button"
                title="View on Makers board"
                aria-label={`View ${employeeNames} on Makers board`}
                onClick={(e) => {
                  e.stopPropagation();
                  stashMakersEmployeeFilter(allEmployeeIds);
                }}
              >
                <BookUser size={16} />
              </Link>
            ) : (
              <button
                className="icon-button"
                type="button"
                disabled
                title="No one assigned"
                aria-label="No one assigned"
              >
                <BookUser size={16} />
              </button>
            )}
            <button
              className="icon-button"
              type="button"
              data-testid="clear-assignees-button"
              disabled={!hasAssignees || saving}
              title={hasAssignees ? "Clear assignees" : "No one assigned"}
              aria-label={
                hasAssignees
                  ? `Clear assignees from ${formatRole(assignment.required_role_id)}`
                  : "No one assigned"
              }
              onClick={(e) => {
                e.stopPropagation();
                void handleClearAssignees();
              }}
            >
              <UserRoundX size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="assignment-row__timeline">
        <TimelineRow
          weeks={weeks}
          className="assignment-row--main"
          totalWeeklyHours={employeeCapacity}
          projectAllocations={[projectAllocation]}
          onHoursChange={(weekKey, newHours, singleCellOnly) =>
            updateHoursForAssignments(assignment, weekKey, newHours, singleCellOnly)
          }
          editable={true}
          projectStartDate={project.start_date}
          projectEndDate={projectEndDate}
          rowId={assignment.id}
          useSharedSelection={true}
          hoursLabel="This project"
          oooByWeekKey={oooByWeekKey}
        />
      </div>
    </div>
  );
}
